import { useFormContext } from 'react-hook-form';
import { UserPhoneNumberFormProps } from '../../props';
import UserPhoneNumberFormCreate from '../../Create';
import UserPhoneNumberFormVerify from '../../Verify';
import { UserPhoneNumberFormLayoutProps } from '../props';
import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { usePhoneNumberForm } from '@starsoft/common/hooks';

/**
 * UserPhoneNumberFormLayoutRenderer Component
 * This component is responsible for rendering the appropriate form layout based on the user's phone number creation status.
 * It decides whether to render the creation form or the verification form.
 *
 * @param {Object} props - The properties for the component.
 * @param {boolean} props.isKycFlow - Indicates if the form is part of the KYC (Know Your Customer) flow.
 * @param {boolean} props.isContainerColor - Determines if the container should have a specific color.
 *
 * @returns {JSX.Element} The rendered form layout component, either for creating or verifying a user phone number.
 *
 * @example
 * // Example usage:
 * <UserPhoneNumberFormLayoutRenderer
 *   isKycFlow={true}
 *   isContainerColor={false}
 * />
 */
export default function UserPhoneNumberFormLayoutRenderer({
  isKycFlow,
  isContainerColor,
}: Pick<UserPhoneNumberFormLayoutProps, 'isKycFlow' | 'isContainerColor'>) {
  // Access form context to manage form state
  const { watch, setValue } = useFormContext<
    UserPhoneNumberFormProps & CreateDepositRequestDto
  >();

  // Custom hook to manage phone number form state and actions
  const { hasCreatedUserPhoneNumber } = usePhoneNumberForm({
    watch,
    setValue,
    enabled: false,
  });

  // Render the creation form if the user has not created a phone number
  if (!hasCreatedUserPhoneNumber) {
    return (
      <UserPhoneNumberFormCreate
        isKycFlow={isKycFlow}
        isContainerColor={isContainerColor}
      />
    );
  }

  // Render the verification form if the user has already created a phone number
  return <UserPhoneNumberFormVerify isContainerColor={isContainerColor} />;
}
